/**
 * Auth Wall — the "Create a profile to unlock the feed" screen from the mobile app.
 *
 * Shown to logged-out visitors instead of a hard redirect to /login.
 * Mirrors screenshot 1 from the mobile app: panda badge, "18+ MATURE COMMUNITY"
 * pill, serif hero copy, big rose "Create profile" pill, 2×2 feature pills,
 * Terms + Privacy footer.
 */
import { Link } from "react-router-dom";
import { Video, Gift, MessageCircle, MapPin } from "lucide-react";

const features = [
  { Icon: Video,         label: "Watch live streams" },
  { Icon: Gift,          label: "Send animated gifts" },
  { Icon: MessageCircle, label: "Chat with friends" },
  { Icon: MapPin,        label: "Meet friends on Social Meet" },
];

export default function AuthWall({ context = "the feed" }) {
  return (
    <div
      data-testid="auth-wall"
      className="min-h-screen w-full bg-black text-white flex items-center justify-center px-5 py-10"
    >
      <div className="w-full max-w-md bg-zinc-950/80 border border-white/8 rounded-3xl p-6 sm:p-8 text-center backdrop-blur-md">
        {/* Panda logo tile */}
        <div className="mx-auto mb-5 w-16 h-16 rounded-2xl bg-gradient-to-br from-rose-500 via-fuchsia-500 to-violet-500 flex items-center justify-center text-3xl shadow-[0_0_40px_rgba(225,29,72,0.35)]">
          🐼
        </div>

        {/* 18+ pill */}
        <div className="inline-block mb-4 text-[11px] uppercase tracking-[0.28em] font-semibold text-rose-400 bg-rose-950/40 border border-rose-500/30 rounded-full px-3 py-1">
          18+ Mature Community
        </div>

        <h1 className="font-display text-3xl sm:text-4xl leading-tight mb-3">
          Create a profile to unlock {context}
        </h1>
        <p className="text-sm text-zinc-400 leading-relaxed mb-6">
          Follow creators, like posts, drop comments, and share your own updates.
        </p>

        <Link
          to="/register"
          data-testid="auth-wall-create-profile"
          className="block w-full rounded-full bg-rose-600 hover:bg-rose-500 text-white font-semibold text-base py-3.5 mb-3 transition-colors shadow-[0_8px_28px_rgba(225,29,72,0.35)]"
        >
          Create profile
        </Link>
        <Link
          to="/login"
          data-testid="auth-wall-signin"
          className="block text-sm font-medium text-white/90 hover:text-white py-2 mb-6"
        >
          I already have an account
        </Link>

        {/* 2×2 feature pills */}
        <div className="grid grid-cols-2 gap-2 mb-6">
          {features.map(({ Icon, label }) => (
            <div
              key={label}
              className="flex items-center gap-2 rounded-full bg-black/50 border border-white/8 px-3 py-2 text-[11px] sm:text-xs text-white/90"
            >
              <span className="w-6 h-6 rounded-full bg-rose-600/25 grid place-items-center shrink-0">
                <Icon size={12} className="text-rose-400" />
              </span>
              <span className="truncate">{label}</span>
            </div>
          ))}
        </div>

        <p className="text-[11px] text-zinc-500 leading-relaxed">
          By continuing you agree to our <Link to="/terms" className="text-rose-400 hover:underline">Terms</Link> and{" "}
          <Link to="/privacy" className="text-rose-400 hover:underline">Privacy Policy</Link>.<br />
          18+ only. No nudity or sexually explicit content.
        </p>
      </div>
    </div>
  );
}
